import { useAuthorizeUser } from "@coworker/apprestructured/src/shared/hooks/useAuthorizeUser";
import { SimpleDefaultLoadingScreen } from "@coworker/apprestructured/src/shared/simple/SimpleLoading/SimpleDefaultLoadingScreen";
import { Error } from "@coworker/apprestructured/src/login/components/Error/Error";
import { ErrorType } from "@coworker/apprestructured/src/shared/constants/errorTypes";
import { ReactElement } from "react";
import { NewUserPage } from "@coworker/apprestructured/src/login/components/NewUserPage/NewUserPage";
import { UnauthorizedPage } from "@coworker/apprestructured/src/login/components/Unauthorized/UnauthorizedPage";
import { useUserHasFixaBasicPermissions } from "@coworker/apprestructured/src/login/hooks/useUserHasRoleOnPlatformOwner";
import useFlag, {
  FLAGS,
  useUserPermissionsQuery,
} from "@coworker/app/src/hooks/useFlag";

export const AuthorizedUserRoute = ({
  children,
}: {
  children: ReactElement;
}) => {
  const userState = useAuthorizeUser();

  // TO-DO: COAPP-12897 - remove the feature flag once deployed to all markets.
  // Wait for flags to load before evaluating — prevents a flash where the user
  // briefly accesses the app before the flag resolves to true.
  const { isLoading: isFlagLoading } = useUserPermissionsQuery();
  const enableAtkomst = useFlag(FLAGS.ENABLE_ATKOMST);

  // Only runs the Atkomst JWT decode when the flag is ON.
  // When flag is OFF: enabled=false → query never fires → no unnecessary Atkomst calls.
  const {
    data: hasFixaBasicPermissions,
    isLoading: isFixaBasicPermissionsLoading,
  } = useUserHasFixaBasicPermissions(enableAtkomst);

  if (
    userState.isLoading ||
    isFlagLoading ||
    (enableAtkomst && isFixaBasicPermissionsLoading)
  ) {
    return <SimpleDefaultLoadingScreen />;
  }

  if (enableAtkomst && !hasFixaBasicPermissions && !userState.isError) {
    return <UnauthorizedPage />;
  }

  // Redirect to NewUserPage for users that are not fully provisioned yet (Åtkomst markets only)
  if (enableAtkomst && userState.errorType === ErrorType.NOTFOUND) {
    return <NewUserPage />;
  }

  if (userState.isError || userState.errorType === ErrorType.FORBIDDEN) {
    return (
      <Error isAccessIssue={userState.errorType === ErrorType.FORBIDDEN} />
    );
  }

  return userState?.isActive ? children : <Error isAccessIssue={false} />;
};
